import React, { useRef, useState, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import Planet from "./Planet";
import { getOrbitPosition } from "../utils/orbitUtils";

// ─── Shared canvas-based radial glow texture ────────────────────────────────
// One texture is created per component mount but they're all identical in shape
// (the spriteMaterial color prop handles per-planet tinting).
const makeGlowTexture = () => {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width  = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const c   = size / 2;
  const grad = ctx.createRadialGradient(c, c, 0, c, c, c);
  grad.addColorStop(0,    "rgba(255,255,255,0.75)");
  grad.addColorStop(0.18, "rgba(255,255,255,0.45)");
  grad.addColorStop(0.45, "rgba(255,255,255,0.12)");
  grad.addColorStop(0.75, "rgba(255,255,255,0.03)");
  grad.addColorStop(1,    "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  return tex;
};

/**
 * OrbitalPlanet — Keplerian orbit + glow halo + HTML label overlay.
 *
 * Each planet gets:
 *   • A colored Sprite glow (AdditivBlending, always faces camera)
 *   • A dim colored PointLight for self-illumination of the PBR surface
 *   • An Html label that tracks the exact 3D position every frame
 */
const OrbitalPlanet = ({
  orbitElements,
  orbitSpeed,
  initialAngle = 0,
  positionCallback,
  frozen   = false,
  name,
  labelColor = "#FFFFFF",
  sectionLabel,
  scale,
  ...planetProps
}) => {
  const groupRef = useRef();
  const angleRef = useRef(initialAngle);
  const [hovered, setHovered] = useState(false);
  const { a, e, inc, omega, Omega } = orbitElements;

  // Glow texture (canvas radial gradient, tinted via spriteMaterial.color)
  const glowTex = useMemo(makeGlowTexture, []);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    if (!frozen) angleRef.current += orbitSpeed * delta;
    const [x, y, z] = getOrbitPosition(a, e, inc, omega, Omega, angleRef.current);
    groupRef.current.position.set(x, y, z);
    if (positionCallback) positionCallback([x, y, z]);
  });

  const isActive  = hovered || frozen;
  const scaleY    = Array.isArray(scale) ? scale[1] : (scale ?? 2);
  const labelY    = scaleY * 2.2;
  // Glow sprite spans ~3× the planet's visual radius
  const glowSize  = scaleY * 9;
  // Self-illumination light: small radius, just enough to brighten the surface
  const lightDist = scaleY * 18;

  return (
    <group
      ref={groupRef}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); }}
      onPointerOut={() => setHovered(false)}
    >
      {/* ── Colored glow halo ─────────────────────────────────────────────── */}
      {/* Sprite auto-faces camera; AdditiveBlending makes it bloom naturally */}
      <sprite scale={[glowSize, glowSize, 1]}>
        <spriteMaterial
          map={glowTex}
          color={labelColor}
          transparent
          opacity={isActive ? 0.85 : 0.55}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </sprite>

      {/* ── Self-illumination point light ─────────────────────────────────── */}
      {/* Lifts the planet's own PBR surface so the GLB texture shows clearly */}
      <pointLight
        color={labelColor}
        intensity={1.2}
        distance={lightDist}
        decay={2}
      />

      {/* ── Planet mesh ───────────────────────────────────────────────────── */}
      <Planet
        {...planetProps}
        scale={scale}
        name={null}
        position={[0, 0, 0]}
        frozen={frozen}
      />

      {/* ── Html label — exact 3D position every frame ────────────────────── */}
      {name && (
        <Html
          position={[0, labelY, 0]}
          center
          distanceFactor={55}
          style={{
            pointerEvents: "none",
            whiteSpace: "nowrap",
            opacity: isActive ? 1 : 0,
            transition: "opacity 0.25s ease",
            userSelect: "none",
            textAlign: "center",
          }}
          zIndexRange={[20, 0]}
        >
          <div
            style={{
              fontFamily: "'Cinzel', serif",
              fontSize: "16px",
              fontWeight: 600,
              color: labelColor,
              letterSpacing: "0.1em",
              textShadow:
                "0 0 12px rgba(0,0,0,1), 0 0 24px rgba(0,0,0,0.9), 0 2px 4px rgba(0,0,0,1)",
            }}
          >
            {name.toUpperCase()}
          </div>
          {sectionLabel && (
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "11px",
                color: "rgba(255,235,250,0.82)",
                marginTop: "4px",
                letterSpacing: "0.08em",
                textShadow: "0 0 8px rgba(0,0,0,1)",
              }}
            >
              {sectionLabel}
            </div>
          )}
        </Html>
      )}
    </group>
  );
};

export default OrbitalPlanet;
